import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  Mic, 
  Sparkles, 
  ArrowRight, 
  User,
  LogOut
} from 'lucide-react';
import { AppConcept } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface LandingPageProps {
  onGenerateIdea: (idea: string) => void;
  onViewGallery: () => void;
  onLogin: () => void;
  recentConcepts: AppConcept[];
}

const DemoAnimation: React.FC = () => {
  const [demoStage, setDemoStage] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [currentIdeaIndex, setCurrentIdeaIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [showMacCursor, setShowMacCursor] = useState(false);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [cursorOverText, setCursorOverText] = useState(false);

  const demoIdeas = [
    {
      input: "What if I could text my plant?",
      concept: {
        icon: "🌱",
        name: "PlantText",
        tagline: "Simple plant care through SMS notifications",
        features: [
          "Basic soil moisture sensor (Arduino + WiFi module)",
          "SMS alerts when plant needs water (Twilio API)",
          "Simple mobile app to receive plant 'messages'",
          "One plant type support (common houseplants)",
          "Basic watering schedule recommendations"
        ]
      }
    },
    {
      input: "What if my pet could order their own food?",
      concept: {
        icon: "🐕",
        name: "PetOrder",
        tagline: "Automated pet food delivery based on consumption",
        features: [
          "Smart food bowl with weight sensors",
          "Mobile app to track daily food consumption",
          "Integration with one pet food supplier (Chewy API)",
          "Simple reorder trigger when food runs low",
          "Basic pet profile (weight, age, food preferences)"
        ]
      }
    },
    {
      input: "What if my water bottle reminded me to drink?",
      concept: {
        icon: "💧",
        name: "HydroAlert",
        tagline: "Smart water bottle with hydration reminders",
        features: [
          "Water bottle with liquid level sensor",
          "Bluetooth connection to smartphone app",
          "Customizable drink reminders based on goals",
          "Daily hydration tracking and simple analytics",
          "LED indicator on bottle for visual reminders"
        ]
      }
    },
    {
      input: "What if my calendar could reschedule itself?",
      concept: {
        icon: "📅",
        name: "AutoCal",
        tagline: "Calendar that suggests optimal meeting times",
        features: [
          "Google Calendar integration for conflict detection",
          "Simple rescheduling suggestions via email",
          "Basic availability sharing with meeting participants",
          "Travel time estimation using Google Maps API",
          "Priority-based meeting importance scoring"
        ]
      }
    },
    {
      input: "What if my headphones chose music based on my heartbeat?",
      concept: {
        icon: "🎧",
        name: "HeartBeats",
        tagline: "Music that adapts to your heart rate",
        features: [
          "Heart rate sensor integration (fitness tracker API)",
          "Spotify playlist switching based on BPM ranges",
          "Simple mood detection (relaxed, active, stressed)",
          "Basic music recommendation engine",
          "Manual override and preference learning"
        ]
      }
    }
  ];

  useEffect(() => {
    const demoSequence = async () => {
      if (demoStage === 0) {
        setTypedText('');
        setIsHighlighting(false);
        await wait(500);
        await typeText(demoIdeas[currentIdeaIndex].input, 50);
        await wait(1000);
        
        // Show cursor and move to generate button
        setShowMacCursor(true);
        await moveCursorToButton();
        await wait(500);
        
        // Click generate button
        await clickButton();
        setDemoStage(1);
      } else if (demoStage === 1) {
        await wait(2500);
        setDemoStage(2);
      } else if (demoStage === 2) {
        await wait(3000);
        
        // Move cursor to text area and highlight text
        await moveCursorToText();
        await wait(500);
        await highlightText();
        await wait(500);
        
        // Move to next idea
        setCurrentIdeaIndex((prev) => (prev + 1) % demoIdeas.length);
        setDemoStage(0);
      }
    };
    demoSequence();
  }, [demoStage, currentIdeaIndex]);

  const typeText = (text: string, speed: number) => {
    return new Promise<void>((resolve) => {
      setTypedText('');
      let i = 0;
      const timer = setInterval(() => {
        if (i < text.length) {
          setTypedText(text.substring(0, i + 1));
          i++;
        } else {
          clearInterval(timer);
          resolve();
        }
      }, speed);
    });
  };

  const wait = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  const moveCursorToButton = () => {
    return new Promise<void>((resolve) => {
      const button = document.querySelector('.demo-generate-btn');
      if (button) {
        const rect = button.getBoundingClientRect();
        const demoRect = document.querySelector('.demo-animation')?.getBoundingClientRect();
        if (demoRect) {
          const targetX = rect.left + rect.width / 2 - demoRect.left;
          const targetY = rect.top + rect.height / 2 - demoRect.top;
          setCursorOverText(false); // Moving away from text
          animateCursor(targetX, targetY, 800, resolve);
        }
      } else {
        resolve();
      }
    });
  };

  const moveCursorToText = () => {
    return new Promise<void>((resolve) => {
      const textarea = document.querySelector('.demo-textarea');
      if (textarea) {
        const rect = textarea.getBoundingClientRect();
        const demoRect = document.querySelector('.demo-animation')?.getBoundingClientRect();
        if (demoRect) {
          const targetX = rect.left + 20 - demoRect.left;
          const targetY = rect.top + 20 - demoRect.top;
          setCursorOverText(true);
          animateCursor(targetX, targetY, 600, resolve);
        }
      } else {
        resolve();
      }
    });
  };

  const animateCursor = (targetX: number, targetY: number, duration: number, callback: () => void) => {
    const startX = cursorPosition.x;
    const startY = cursorPosition.y;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth movement
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      const currentX = startX + (targetX - startX) * easeProgress;
      const currentY = startY + (targetY - startY) * easeProgress;
      
      setCursorPosition({ x: currentX, y: currentY });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        callback();
      }
    };
    
    requestAnimationFrame(animate);
  };

  const clickButton = () => {
    return new Promise<void>((resolve) => {
      // Add click animation
      const cursor = document.querySelector('.mac-cursor');
      if (cursor) {
        cursor.classList.add('clicking');
        setTimeout(() => {
          cursor.classList.remove('clicking');
          resolve();
        }, 200);
      } else {
        resolve();
      }
    });
  };

  const highlightText = () => {
    return new Promise<void>((resolve) => {
      setIsHighlighting(true);
      setTimeout(() => {
        setIsHighlighting(false);
        resolve();
      }, 1000);
    });
  };

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <div className="demo-animation">
      <div className="demo-input-section">
        <label>What's your shower thought?</label>
        <div 
          className={`demo-textarea ${isHighlighting ? 'highlighting' : ''}`}
          data-text={typedText}
        >
          {typedText}
          {showCursor && <span className="demo-cursor">|</span>}
        </div>
        <div className="demo-button-container">
          <button className="demo-generate-btn">
            <span>✨</span>
            Generate Concept
          </button>
        </div>
        {demoStage === 1 && (
          <div className="ai-processing">
            <div className="processing-dots">
              <span></span><span></span><span></span>
            </div>
            <span>AI is refining your idea...</span>
          </div>
        )}
      </div>
      
      {/* macOS Cursor */}
      {showMacCursor && (
        <div 
          className="mac-cursor"
          style={{
            left: `${cursorPosition.x}px`,
            top: `${cursorPosition.y}px`,
          }}
        >
          {cursorOverText ? (
            // Text selection I-beam cursor
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M8 3L12 3M10 3L10 17M8 17L12 17"
                stroke="black"
                strokeWidth="1.2"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M8 3L12 3M10 3L10 17M8 17L12 17"
                stroke="white"
                strokeWidth="0.6"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          ) : (
            // Regular arrow cursor
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 2.5L17 11L12 12.5L9.5 18L4 2.5Z"
                fill="white"
                stroke="black"
                strokeWidth="1"
                strokeLinejoin="round"
              />
              <path
                d="M4 2.5L17 11L12 12.5L9.5 18L4 2.5Z"
                fill="none"
                stroke="rgba(0,0,0,0.3)"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      )}
      
      {demoStage === 2 && (
        <div className="demo-result">
          <div className="result-header">
            <div className="result-status">
              <div className="status-icon">✨</div>
              <span>Concept Generated</span>
            </div>
          </div>
          <div className="result-card">
            <div className="result-top">
              <div className="result-icon">{demoIdeas[currentIdeaIndex].concept.icon}</div>
              <div className="result-title-section">
                <h4>{demoIdeas[currentIdeaIndex].concept.name}</h4>
                <p className="result-tagline">"{demoIdeas[currentIdeaIndex].concept.tagline}"</p>
              </div>
            </div>
            <div className="result-features">
              {demoIdeas[currentIdeaIndex].concept.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-bullet">•</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
            <div className="result-tags">
              {currentIdeaIndex === 0 && (
                <>
                  <span>Arduino</span>
                  <span>SMS API</span>
                  <span>IoT</span>
                  <span>Mobile</span>
                </>
              )}
              {currentIdeaIndex === 1 && (
                <>
                  <span>Hardware</span>
                  <span>API Integration</span>
                  <span>E-commerce</span>
                  <span>Pet Care</span>
                </>
              )}
              {currentIdeaIndex === 2 && (
                <>
                  <span>Bluetooth</span>
                  <span>Sensors</span>
                  <span>Health</span>
                  <span>Mobile App</span>
                </>
              )}
              {currentIdeaIndex === 3 && (
                <>
                  <span>Calendar API</span>
                  <span>SaaS</span>
                  <span>Productivity</span>
                  <span>Automation</span>
                </>
              )}
              {currentIdeaIndex === 4 && (
                <>
                  <span>Music API</span>
                  <span>Wearables</span>
                  <span>Audio</span>
                  <span>Health Tech</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LandingPage: React.FC<LandingPageProps> = ({ onGenerateIdea: _onGenerateIdea, onLogin: _onLogin }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [userIdea, setUserIdea] = useState('');
  const [showIdeaInput, setShowIdeaInput] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);

  const placeholders = [
    "What if I could text my plant?",
    "What if my pet could order their own food?",
    "What if my water bottle reminded me to drink?",
    "What if my calendar could reschedule itself?",
    "What if my headphones chose music based on my heartbeat?",
    "What if my toothbrush rated my brushing?"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleVoiceInput = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setUserIdea(transcript);
        setIsListening(false);
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } else {
      alert('Voice recognition not supported in this browser');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userIdea.trim()) {
      // Navigate to generate page with the idea
      navigate('/generate', { state: { userIdea: userIdea.trim() } });
    }
  };

  const handleGetStarted = () => {
    if (currentUser) {
      // User is logged in, show idea input form
      setShowIdeaInput(true);
      setTimeout(() => {
        document.getElementById('idea-input')?.focus();
      }, 100);
    } else {
      // User not logged in, redirect to login
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (showIdeaInput) {
    return (
      <div className="idea-input-page">
        <div className="input-container-page">
          <div className="input-header">
            <button 
              onClick={() => setShowIdeaInput(false)} 
              className="back-link"
            >
              ← Back to Home
            </button>
            <h1>Share Your Idea</h1>
            <p>Describe any random thought, even if it sounds silly. Our AI will transform it into a complete app concept.</p>
          </div>

          <form onSubmit={handleSubmit} className="professional-input-form">
            <div className="input-group">
              <label htmlFor="idea-input">What's your idea?</label>
              <textarea
                id="idea-input"
                value={userIdea}
                onChange={(e) => setUserIdea(e.target.value)}
                placeholder={placeholders[placeholderIndex]}
                className="professional-textarea"
                rows={6}
              />
              <div className="input-footer">
                <span className="character-count">{userIdea.length}/500</span>
                <button
                  type="button"
                  onClick={handleVoiceInput}
                  className={`voice-btn ${isListening ? 'listening' : ''}`}
                  disabled={isListening}
                >
                  <Mic size={16} />
                  {isListening ? 'Listening...' : 'Voice'}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              className="generate-cta"
              disabled={!userIdea.trim() || userIdea.length > 500}
            >
              <Sparkles size={20} />
              Generate App Concept
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="input-examples">
            <h3>Need inspiration? Try these:</h3>
            <div className="example-pills">
              <button onClick={() => setUserIdea("What if I could text my plant?")} className="example-pill">
                Plant texting
              </button>
              <button onClick={() => setUserIdea("What if my pet could order their own food?")} className="example-pill">
                Pet food ordering
              </button>
              <button onClick={() => setUserIdea("What if my water bottle reminded me to drink?")} className="example-pill">
                Smart hydration
              </button>
              <button onClick={() => setUserIdea("What if my headphones chose music based on my heartbeat?")} className="example-pill">
                Heartbeat music
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="professional-landing">
      {/* Navigation */}
      <nav className="professional-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <Brain size={28} />
            <span>ShowerThoughts</span>
          </div>
          <div className="nav-links">
            {currentUser ? (
              <div className="nav-user-section">
                <span className="nav-user-info">
                  <User size={16} />
                  {currentUser.displayName || currentUser.email}
                </span>
                <button onClick={handleLogout} className="nav-logout">
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            ) : (
              <button onClick={() => navigate('/login')} className="nav-cta">
                Get Started Free
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} />
            <span>AI-Powered Innovation</span>
          </div>
          <h1 className="hero-heading">
            Turn Random Ideas Into 
            <span className="gradient-text"> Startup Concepts</span>
          </h1>
          <p className="hero-description">
            Transform your shower thoughts into detailed app concepts with our AI-powered ideation platform. 
            Get complete business plans, feature lists, and technical recommendations in 60 seconds.
          </p>
          
          <div className="hero-actions">
            <button onClick={handleGetStarted} className="primary-cta">
              <Sparkles size={20} />
              Generate Your First Idea
              <ArrowRight size={16} />
            </button>
          </div>

          <div className="hero-social-proof">
            <div className="social-item">
              <strong>Powered by Innovation</strong>
              <span>Creative Thinking</span>
            </div>
            <div className="social-item">
              <strong>AI-Powered</strong>
              <span>Multi-stage Analysis</span>
            </div>
            <div className="social-item">
              <strong>60 Seconds</strong>
              <span>Idea to Concept</span>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="demo-browser">
            <div className="browser-header">
              <div className="browser-dots">
                <span className="dot red"></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
              </div>
              <div className="browser-url">
                <span>showerthoughts.ai</span>
              </div>
            </div>
            <div className="browser-content">
              <DemoAnimation />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;